import { DataTableHeaderCell } from '@/_shared/components/data-table/header/index.js';
import { DataTable } from '@/_shared/components/data-table/index.js';
import { Hint } from '@/_shared/components/hint/index.js';
import { useTsRest } from '@/_shared/context/ts-rest.js';
import { Body1, Button, Dialog, DialogSurface, Dropdown, Option, Spinner, tokens } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { createColumnHelper } from '@tanstack/react-table';
import type { PaymentDocument } from '@this/shared/dist/apis/serviceaddinapi/payment-documents.js';
import { toHumanReadable } from '@this/shared/dist/tools/datetime.js';
import { formatEur } from '@this/shared/dist/tools/number-format.js';
import { useEffect, useMemo, useState } from 'react';
import { DocumentViewer } from '../claim-details/document-viewer.js';
import { useStyles } from './_styles.js';

const columnHelper = createColumnHelper<PaymentDocument>();

const LAYOUT_BREAKPOINT = 1280;

type LayoutMode = 'wide' | 'narrow';

export function PaymentDocuments() {
    const styles = useStyles();
    const tsr = useTsRest();
    const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[] | null>(null);
    const [layoutMode, setLayoutMode] = useState<LayoutMode>('wide');

    // Year filter state
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState<number>(currentYear);

    // Generate array of 7 years (current year and 6 previous years)
    const yearOptions = useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => currentYear - i);
    }, [currentYear]);

    // Detect layout mode based on window width
    useEffect(() => {
        const checkLayoutMode = () => {
            setLayoutMode(window.innerWidth >= LAYOUT_BREAKPOINT ? 'wide' : 'narrow');
        };

        // Initial check
        checkLayoutMode();

        // Listen for resize events
        window.addEventListener('resize', checkLayoutMode);

        return () => {
            window.removeEventListener('resize', checkLayoutMode);
        };
    }, []);

    // Handle closing the document dialog
    const handleCloseDocumentDialog = () => {
        setSelectedDocumentIds(null);
    };

    // Determine if document dialog should be shown
    const isDocumentDialogOpen =
        layoutMode === 'narrow' && selectedDocumentIds !== null && selectedDocumentIds.length > 0;

    const { data, isLoading, error } = tsr.paymentDocuments.getPaymentDocuments.useQuery({
        queryKey: ['payment-documents', year],
        queryData: {
            query: { year },
        },
        refetchInterval: 30_000,
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('processedAt', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Verarbeitet am"
                    />
                ),
                cell: (info) =>
                    toHumanReadable(new Date(info.row.original.processedAt), 'datetime_short_with_seconds_2digit'),
                filterFn: 'dateFilter',
                meta: {
                    filter: { type: 'date' },
                },
            }),
            columnHelper.accessor('claimNumber', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Schadennummer"
                    />
                ),
                cell: (info) => info.row.original.claimNumber,
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('contractNumber', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Vertrag"
                        tooltip="Vertragsnummer"
                    />
                ),
                cell: (info) => info.row.original.contractNumber || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('policyHolder', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Versicherungsnehmer"
                    />
                ),
                cell: (info) => info.row.original.policyHolder || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('documentType', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Belegtyp"
                    />
                ),
                cell: (info) => info.row.original.documentType,
                filterFn: 'selectFilter',
                meta: {
                    filter: {
                        type: 'select',
                        options: [
                            { value: 'Rechnung', label: 'Rechnung' },
                            { value: 'Kostenvoranschlag', label: 'Kostenvoranschlag' },
                            { value: 'Eigenleistung', label: 'Eigenleistung' },
                        ],
                    },
                },
            }),
            columnHelper.accessor('documentNumber', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Belegnummer"
                    />
                ),
                cell: (info) => info.row.original.documentNumber || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('businessName', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Firmenname"
                    />
                ),
                cell: (info) => info.row.original.businessName || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('documentAmount', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Betrag"
                    />
                ),
                cell: (info) => formatEur(info.row.original.documentAmount),
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
            columnHelper.accessor('decision', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Entscheidung"
                    />
                ),
                cell: (info) => info.row.original.decision,
                filterFn: 'selectFilter',
                meta: {
                    filter: {
                        type: 'select',
                        options: [
                            { value: 'Freigabe', label: 'Freigabe' },
                            { value: 'Regulierung', label: 'Regulierung' },
                            { value: 'Ablehnung', label: 'Ablehnung' },
                        ],
                    },
                },
            }),
            columnHelper.accessor('payoutAmount', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Auszahlung"
                    />
                ),
                cell: (info) =>
                    info.row.original.payoutAmount !== null ? formatEur(info.row.original.payoutAmount) : '—',
                filterFn: 'numberFilter',
                meta: {
                    filter: { type: 'number' },
                },
            }),
            columnHelper.accessor('payoutRecipient', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Zahlungsempfänger"
                    />
                ),
                cell: (info) => info.row.original.payoutRecipient || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('payoutIban', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Bankverbindung"
                    />
                ),
                cell: (info) => info.row.original.payoutIban || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
            columnHelper.accessor('payoutReference', {
                header: (context) => (
                    <DataTableHeaderCell
                        tableId="payment-documents"
                        table={context.table}
                        column={context.column}
                        title="Zahlungsreferenz"
                    />
                ),
                cell: (info) => info.row.original.payoutReference || '—',
                filterFn: 'stringFilter',
                meta: {
                    filter: { type: 'string' },
                },
            }),
        ],
        []
    );

    if (isLoading) {
        return (
            <div className={styles.page} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Spinner size="large" label="Lade Zahlungsdokumente..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <Body1 style={{ color: 'var(--colorPaletteRedForeground1)' }}>
                    Fehler beim Laden der Daten: {error instanceof Error ? error.message : 'Unbekannter Fehler'}
                </Body1>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <div className={styles.leftColumn}>
                    <DataTable<PaymentDocument, any>
                        id="payment-documents"
                        tableLabelNode={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <div>Zahlungsdokumente verarbeitet im Jahr</div>
                                <Dropdown
                                    selectedOptions={[year.toString()]}
                                    value={year.toString()}
                                    onOptionSelect={(_, d) =>
                                        setYear(parseInt(d.optionValue || currentYear.toString(), 10))
                                    }
                                    size="small"
                                    style={{
                                        minWidth: 0,
                                        borderBottomColor: tokens.colorNeutralStroke1,
                                    }}
                                >
                                    {yearOptions.map((y) => (
                                        <Option key={y} value={y.toString()} text={y.toString()}>
                                            {y}
                                        </Option>
                                    ))}
                                </Dropdown>
                                <Hint text="Zahlungsbelege und Belegsummen erscheinen, sobald diese von der KI oder einem Sachbearbeiter bearbeitet wurden." />
                            </div>
                        }
                        loading={isLoading}
                        data={data?.body.documents}
                        columns={columns as any}
                        getItemId={(item) => item.id}
                        columnTitles={{
                            processedAt: 'Verarbeitet am',
                            claimNumber: 'Schadennummer',
                            contractNumber: 'Vertrag',
                            policyHolder: 'Versicherungsnehmer',
                            documentType: 'Belegtyp',
                            documentNumber: 'Belegnummer',
                            businessName: 'Firmenname',
                            documentAmount: 'Betrag',
                            decision: 'Entscheidung',
                            payoutAmount: 'Auszahlung',
                            payoutRecipient: 'Zahlungsempfänger',
                            payoutIban: 'Bankverbindung',
                            payoutReference: 'Zahlungsreferenz',
                        }}
                        defaultSorting={[
                            {
                                id: 'processedAt',
                                desc: true,
                            },
                        ]}
                        generateCustomToolbarCounts={(rows) => [
                            {
                                label: 'Anzahl Dokumente',
                                count: rows.length,
                            },
                        ]}
                        dataExportFunc={(documents) => {
                            const header: string[][] = [
                                [
                                    'Verarbeitet am',
                                    'Schadennummer',
                                    'Vertrag',
                                    'Versicherungsnehmer',
                                    'Belegtyp',
                                    'Belegnummer',
                                    'Firmenname',
                                    'Betrag',
                                    'Entscheidung',
                                    'Auszahlung',
                                    'Zahlungsempfänger',
                                    'Bankverbindung',
                                    'Zahlungsreferenz',
                                ],
                            ];
                            const data: string[][] = documents.map((doc) => [
                                toHumanReadable(new Date(doc.processedAt), 'datetime_short_with_seconds_2digit'),
                                doc.claimNumber,
                                doc.contractNumber || '-',
                                doc.policyHolder || '-',
                                doc.documentType,
                                doc.documentNumber || '-',
                                doc.businessName || '-',
                                formatEur(doc.documentAmount),
                                doc.decision,
                                doc.payoutAmount !== null ? formatEur(doc.payoutAmount) : '-',
                                doc.payoutRecipient || '-',
                                doc.payoutIban || '-',
                                doc.payoutReference || '-',
                            ]);
                            return {
                                identifier: 'Zahlungsdokumente',
                                header,
                                data,
                            };
                        }}
                        onRowSelectionChange={(selectedIds) => {
                            if (selectedIds.length > 0) {
                                setSelectedDocumentIds([`virtual-payment-decision-document:${selectedIds[0]}`]);
                            }
                        }}
                    />
                </div>

                {layoutMode === 'wide' && (
                    <div className={styles.rightColumn}>
                        <DocumentViewer documentIds={selectedDocumentIds} />
                    </div>
                )}
            </div>

            <Dialog
                open={isDocumentDialogOpen}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        handleCloseDocumentDialog();
                    }
                }}
            >
                <DialogSurface className={styles.documentDialog} backdrop={{ className: styles.dialogBackdrop }}>
                    <Button
                        appearance="subtle"
                        shape="circular"
                        icon={<DismissRegular />}
                        onClick={handleCloseDocumentDialog}
                        className={styles.dialogCloseButton}
                        aria-label="Schließen"
                    />
                    <DocumentViewer documentIds={selectedDocumentIds} />
                </DialogSurface>
            </Dialog>
        </div>
    );
}
